/**
 * Publishing plan — what goes out on Skida's own channels.
 *
 * Same list pattern as leads, events and ambassadors — and now literally the
 * same table: search, a Mine chip, a Table/Kanban toggle, header filters, a
 * column picker, and a row that opens the same editor a card does.
 *
 * It was the last hand-written grid in the app. The two cells that kept it out
 * of `DataTable` were the six-dot channel row and the link-and-attachment cell,
 * and rather than special-case this screen inside the shared table, `Cell` gained
 * one narrow escape hatch — `node`. See `table.tsx` for why the dots cannot be
 * text, and `DataTable` for why the hatch is deliberately narrow.
 *
 * The phone keeps `MobilePostCards` rather than the shared card: it is the one
 * card the design actually drew, and it shows a near-term window with channel
 * dots and a time rather than the whole list.
 *
 * Search reaches linked entities as well as the post's own fields, which the
 * handoff asks for specifically — typing an event name should find the posts
 * promoting it, and that only works if the names are folded into the haystack
 * at search time rather than stored on the post.
 */
import { useCallback, useEffect, useMemo, useState } from 'react'
import { subscribeToPosts, createPost, updatePost, deletePost } from './repository/postsRepository'
import { subscribeToEvents } from '@/features/events/repository/eventsRepository'
import { subscribeToPartners } from '@/features/partners/repository/partnersRepository'
import type { CrmEvent, Person } from '@/types/events'
import {
  CHANNELS,
  DEFAULT_POST_OWNER_EMAIL,
  EMPTY_POST,
  POST_STATUSES,
  whenLabel,
  postStatusStyle,
  type Post,
} from '@/types/posts'
import { ViewToggle, useViewMode } from '@/shared/components/ViewToggle'
import { KanbanBoard, type KanbanCard } from '@/shared/components/KanbanBoard'
import { haystack, matchesTokens } from '@/shared/util/tokenSearch'
import { useAuth } from '@/features/auth/AuthProvider'
import { useTeamMembers } from '@/features/auth/useTeamMembers'
import { ColumnPicker, DataTable } from '@/shared/components/DataTable'
import type { DataRow, FilterOptions } from '@/shared/components/DataTable'
import { useTableState } from '@/shared/hooks/useTableState'
import {
  buildCells,
  CHANNEL_OPTIONS,
  COLUMN_KEYS,
  COLUMN_META,
  DEFAULT_COLUMNS,
  FILTERABLE_COLUMNS,
  filterValues,
  passesFilters,
  sortPosts,
  type ColumnKey,
  type SortKey,
} from './table'
import { PostCalendar } from './PostCalendar'
import { PostModal } from './PostModal'
import { MobilePostCards } from './MobilePostCards'
import { useIsMobile } from '@/shared/nav/useIsMobile'
import './PublishingWorkspace.css'

const COLUMNS_KEY = 'skidaCrm.postColumns'

export function PublishingWorkspace() {
  const { user } = useAuth()
  const currentUser = user?.displayName ?? ''

  /**
   * Who a post can be assigned to, and who it starts on.
   *
   * The field holds a display name — the same string the Mine chip compares
   * against your own — so the address the default is written by is resolved
   * through the team rather than stored. Nobody, when Lara has no CRM record:
   * see `DEFAULT_POST_OWNER_EMAIL`.
   */
  const team = useTeamMembers()
  const defaultOwner = useMemo(
    () => team.find((m) => m.email === DEFAULT_POST_OWNER_EMAIL)?.displayName ?? '',
    [team],
  )

  const [posts, setPosts] = useState<Post[]>([])
  const [events, setEvents] = useState<CrmEvent[]>([])
  const [people, setPeople] = useState<Person[]>([])
  const [partners, setPartners] = useState<{ id: string; name: string; kind: string }[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const [term, setTerm] = useState('')
  const [mineOnly, setMineOnly] = useState(false)
  const [statusFilter, setStatusFilter] = useState<Post['status'] | 'all'>('all')
  const [viewMode, setViewMode] = useViewMode('calendar')
  const [editing, setEditing] = useState<Post | 'new' | null>(null)
  const isMobile = useIsMobile()

  const t = useTableState<ColumnKey>({
    storageKey: COLUMNS_KEY,
    allKeys: COLUMN_KEYS,
    defaults: DEFAULT_COLUMNS,
    initialSort: 'when',
  })

  useEffect(() => {
    const un = subscribeToPosts(
      (p) => {
        setPosts(p)
        setLoading(false)
      },
      (e) => {
        setError(e.message)
        setLoading(false)
      },
    )
    return un
  }, [])

  // Events and people are read for the pickers and for the "Linked to" column.
  useEffect(() => subscribeToEvents(
    (d) => {
      setEvents(d.events)
      setPeople(d.people.filter((p) => p.role === 'ambassador' || p.role === 'pilot'))
    },
    () => {},
  ), [])

  // Partners, for the link picker and for search. Read once rather than
  // subscribed: the list is thirteen names and changes about once a month.
  useEffect(() => {
    subscribeToPartners(
      (p) => setPartners(p.map((x) => ({ id: x.id, name: x.name, kind: x.kind }))),
      () => {},
    )
  }, [])

  const eventName = useMemo(() => {
    const m = new Map(events.map((e) => [e.id, e.name]))
    return (id: string) => m.get(id) ?? ''
  }, [events])

  const personName = useMemo(() => {
    const m = new Map(people.map((p) => [p.id, p.name]))
    return (id: string) => m.get(id) ?? ''
  }, [people])

  const partnerName = useMemo(() => {
    const m = new Map(partners.map((p) => [p.id, p.name]))
    return (id: string) => m.get(id) ?? ''
  }, [partners])

  /** "Alpine Guide Expo · Tom Ainsley · Fri Flyt" — row and search both use it. */
  const linkedLabel = useCallback(
    (p: Post) =>
      [
        eventName(p.linkedEventId),
        ...p.linkedAmbassadorIds.map(personName),
        ...p.linkedPartnerIds.map(partnerName),
      ]
        .filter(Boolean)
        .join(' · '),
    [eventName, personName, partnerName],
  )

  const rows = useMemo(() => {
    const matched = posts
      .filter((p) => statusFilter === 'all' || p.status === statusFilter)
      .filter((p) => !mineOnly || p.owner === currentUser)
      .filter((p) => passesFilters(p, t.filters))
      .filter((p) =>
        matchesTokens(term, (token) =>
          haystack([
            p.type,
            p.owner,
            p.status,
            p.link,
            p.notes,
            p.channels.join(' '),
            linkedLabel(p),
            p.assets.map((a) => `${a.title} ${a.text}`).join(' '),
          ]).includes(token),
        ),
      )
    return sortPosts(matched, t.sortBy as SortKey, t.sortDir, linkedLabel)
  }, [posts, statusFilter, mineOnly, currentUser, term, linkedLabel, t.filters, t.sortBy, t.sortDir])

  /**
   * Filter options come from every post, not from what is visible — a dropdown
   * whose options disappear as you use it is one you cannot undo. Channels are
   * the full six whatever is in use, because an empty channel is a finding.
   */
  const filterOptions = useMemo((): FilterOptions<ColumnKey> => {
    const distinct = (key: ColumnKey) =>
      [...new Set(posts.flatMap((p) => filterValues(p, key)).filter(Boolean))]
        .sort((a, b) => a.localeCompare(b))
        .map((v) => ({ value: v, label: v }))
    return {
      channels: CHANNEL_OPTIONS,
      type: distinct('type'),
      status: POST_STATUSES.map((s) => ({ value: s.key, label: s.label })),
      owner: distinct('owner'),
    }
  }, [posts])

  const dataRows = useMemo(
    (): DataRow<ColumnKey>[] =>
      rows.map((p) => {
        const st = postStatusStyle(p.status)
        return {
          id: p.id,
          // A post carries a subject now, and that is its name. Before subjects
          // existed the date stood in, which left every undated idea in the
          // plan identified only by "No date yet".
          title: p.subject || p.type,
          subtitle: whenLabel(p),
          cells: buildCells(p, t.columns, linkedLabel(p)),
          card: {
            status: { label: st.label, bg: st.bg, color: st.color },
            lines: [p.channels.join(', ') || 'no channel yet', linkedLabel(p)],
          },
        }
      }),
    [rows, t.columns, linkedLabel],
  )

  const counts = useMemo(() => {
    const m = new Map<string, number>()
    for (const p of posts) m.set(p.status, (m.get(p.status) ?? 0) + 1)
    return m
  }, [posts])

  const save = async (draft: Omit<Post, 'id' | 'createdAt' | 'updatedAt'>, id?: string) => {
    if (id) await updatePost(id, draft)
    else await createPost(draft)
    setEditing(null)
  }

  return (
    <main className="pub">
      <div className="pub__head">
        <div>
          <h2 className="pub__title" data-onb="mkt-header">Publishing plan</h2>
          <p className="pub__sub">
            {loading ? 'Loading…' : `${posts.length} posts · ${rows.length} shown`}
          </p>
        </div>
        <button
          className="btn btn--primary"
          data-onb="mkt-newpost"
          onClick={() => setEditing('new')}
        >
          + New post
        </button>
      </div>

      {error && <div className="empty-note">Could not read the publishing plan: {error}</div>}

      <div className="pub__filters" onClick={() => t.setOpenFilter(null)}>
        <input
          className="input pub__search"
          data-onb="mkt-filters"
          value={term}
          onChange={(e) => setTerm(e.target.value)}
          placeholder="Type, channel, owner, or a linked event or ambassador…"
          aria-label="Search posts"
        />
        <button className="chip" aria-pressed={mineOnly} onClick={() => setMineOnly(!mineOnly)}>
          Mine
        </button>
        <button
          className="chip"
          aria-pressed={statusFilter === 'all'}
          onClick={() => setStatusFilter('all')}
        >
          All
        </button>
        {POST_STATUSES.map((s) => (
          <button
            key={s.key}
            className="chip"
            aria-pressed={statusFilter === s.key}
            onClick={() => setStatusFilter(s.key)}
          >
            {s.label} ({counts.get(s.key) ?? 0})
          </button>
        ))}
        {t.filterCount > 0 && (
          <button className="chip" onClick={t.clearAllFilters}>
            Clear {t.filterCount} filter{t.filterCount === 1 ? '' : 's'}
          </button>
        )}
        <div className="colpickwrap">
          <button
            className="chip"
            onClick={(ev) => {
              ev.stopPropagation()
              t.setShowPicker(!t.showPicker)
            }}
            aria-expanded={t.showPicker}
          >
            Columns ({t.columns.length})
          </button>
          {t.showPicker && (
            <ColumnPicker
              meta={COLUMN_META}
              columns={t.columns}
              onToggle={t.toggleColumn}
              onClose={() => t.setShowPicker(false)}
            />
          )}
        </div>
        <ViewToggle
          value={viewMode}
          onChange={setViewMode}
          label="Publishing plan"
          withCalendar
        />
      </div>

      {isMobile ? (
        <MobilePostCards
          rows={rows}
          narrowed={term.trim() !== '' || mineOnly || statusFilter !== 'all'}
          onEdit={setEditing}
        />
      ) : viewMode === 'calendar' ? (
        <PostCalendar
          posts={rows}
          onSelect={(id) => setEditing(posts.find((p) => p.id === id) ?? null)}
          emptyNote={
            loading
              ? 'Loading the plan…'
              : term.trim() || t.filterCount > 0 || mineOnly || statusFilter !== 'all'
                ? 'No post matches these filters. Clear one to see the rest of the plan.'
                : 'No posts planned yet.'
          }
        />
      ) : viewMode === 'kanban' ? (
        <KanbanBoard
          columns={POST_STATUSES.map((s) => ({ key: s.key, label: s.label, color: s.color }))}
          cards={rows.map((p) => toCard(p, linkedLabel(p)))}
          onSelect={(id) => setEditing(posts.find((p) => p.id === id) ?? null)}
          emptyNote={loading ? 'Loading…' : 'No post matches these filters. Clear one to see the rest of the plan.'}
        />
      ) : (
        <DataTable
          meta={COLUMN_META}
          columns={t.columns}
          rows={dataRows}
          identityLabel="Post"
          identitySortKey="post"
          identityWidth="1.2fr"
          sortBy={t.sortBy}
          sortDir={t.sortDir}
          onSort={t.onSort}
          filterable={FILTERABLE_COLUMNS}
          filters={t.filters}
          filterOptions={filterOptions}
          onToggleFilter={t.toggleFilter}
          onClearFilter={t.clearFilter}
          openFilter={t.openFilter}
          onOpenFilter={t.setOpenFilter}
          selectedId={editing && editing !== 'new' ? editing.id : null}
          onSelect={(id) => setEditing(posts.find((p) => p.id === id) ?? null)}
          loading={loading}
          loadingNote="Loading the plan…"
          emptyNote={
            term.trim() || t.filterCount > 0 || mineOnly || statusFilter !== 'all'
              ? 'No post matches these filters. Clear one to see the rest of the plan.'
              : 'No posts planned yet.'
          }
        />
      )}

      {editing && (
        <PostModal
          post={editing === 'new' ? { id: '', ...EMPTY_POST, owner: defaultOwner } : editing}
          isNew={editing === 'new'}
          events={events}
          people={people}
          partners={partners}
          team={team}
          defaultOwner={defaultOwner}
          onClose={() => setEditing(null)}
          onSave={save}
          onDelete={async (id) => {
            await deletePost(id)
            setEditing(null)
          }}
        />
      )}
    </main>
  )
}

function toCard(p: Post, linked: string): KanbanCard {
  return {
    id: p.id,
    group: p.status,
    title: p.subject || p.type,
    meta: whenLabel(p),
    badge:
      p.channels.length > 0
        ? {
            text: p.channels.length === 1 ? p.channels[0]! : `${p.channels.length} channels`,
            bg: 'var(--color-surface-muted)',
            color: 'var(--color-text-muted)',
          }
        : null,
    trailing: linked || p.owner,
  }
}

export { CHANNELS }
